import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, FormControl, FormControlLabel, InputLabel, MenuItem,
  Select, Switch, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  fetchPiletaConfig,
  putPiletaConfig,
  patchPiletaDevice,
  testPiletaCredentials,
  testPiletaMqtt,
  suggestPiletaMqttFromPi,
  type PiletaCredProfile,
} from '../../services/piletaApi';
import { getRooms } from '../../services/api';
import { eventBus } from '../../services/EventBus';
import { cargarPileta } from '../../services/piletaState';

const ACCENT = '#2391FF';
const CARD_BG = '#1a1a1a';
const MASK = '********';

interface Props {
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
}

/** Estilo Sonos/Android: campos con aire; label shrink para no chocar con el borde. */
const fieldSx = {
  mb: 1.75,
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#0f0f0f',
    color: '#fff',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
  '& .MuiInputLabel-root': { color: '#999' },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiInputBase-input': { color: '#fff' },
  '& .MuiInputBase-input::placeholder': { color: '#666', opacity: 1 },
} as const;

const sectionSx = {
  backgroundColor: CARD_BG,
  borderRadius: 1,
  p: 1.5,
  mb: 1.5,
  border: '1px solid #333',
} as const;

const sectionTitleSx = {
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '#fff',
  mb: 1.25,
} as const;

const PiletaDiscoveryTab: React.FC<Props> = ({
  isDemoUser,
  onDemoActionAttempt,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState<PiletaCredProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [rooms, setRooms] = useState<Array<{ id: number; nombre: string }>>([]);

  const [displayName, setDisplayName] = useState('Pileta / Riego');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('8080');
  const [adminUser, setAdminUser] = useState('admin');
  const [adminPass, setAdminPass] = useState('');
  const [userUser, setUserUser] = useState('user');
  const [userPass, setUserPass] = useState('');
  const [habitacionId, setHabitacionId] = useState<number | ''>('');
  const [adminPassMasked, setAdminPassMasked] = useState(false);
  const [userPassMasked, setUserPassMasked] = useState(false);
  const [testAdmin, setTestAdmin] = useState<string | null>(null);
  const [testUser, setTestUser] = useState<string | null>(null);
  const [mqttEnabled, setMqttEnabled] = useState(true);
  const [mqttPort, setMqttPort] = useState('1883');
  const [mqttUser, setMqttUser] = useState('admin');
  const [mqttPass, setMqttPass] = useState('');
  const [mqttPassMasked, setMqttPassMasked] = useState(false);
  const [mqttTopic, setMqttTopic] = useState('pileta-riego/arduino/estado/pwa');
  const [testingMqtt, setTestingMqtt] = useState(false);
  const [importingMqtt, setImportingMqtt] = useState(false);
  const [testMqttMsg, setTestMqttMsg] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    // Si una sesión pileta vieja dejó el JWT SHC en backup, restaurarlo
    try {
      const backup = localStorage.getItem('__shc_jwt_backup');
      if (backup) {
        localStorage.setItem('token', backup);
        localStorage.removeItem('__shc_jwt_backup');
      }
    } catch { /* ignore */ }

    try {
      const [cfg, roomList] = await Promise.all([
        fetchPiletaConfig(),
        getRooms().catch(() => []),
      ]);
      setRooms(
        [...(roomList || [])]
          .map((r: any) => ({ id: r.id, nombre: r.nombre || String(r.id) }))
          .sort((a, b) => a.nombre.localeCompare(b.nombre)),
      );
      setDisplayName(cfg.display_name || 'Pileta / Riego');
      setHost(cfg.host || '');
      setPort(String(cfg.port || 8080));
      setAdminUser(cfg.admin_username || 'admin');
      setUserUser(cfg.user_username || 'user');
      setHabitacionId(cfg.habitacion_id ?? '');
      setMqttEnabled(cfg.mqtt_enabled !== false);
      setMqttPort(String(cfg.mqtt_port || 1883));
      setMqttUser(cfg.mqtt_username || 'admin');
      setMqttTopic(cfg.mqtt_topic_pwa || 'pileta-riego/arduino/estado/pwa');
      if (cfg.admin_password === MASK) {
        setAdminPass(MASK);
        setAdminPassMasked(true);
      } else {
        setAdminPass(cfg.admin_password || '');
        setAdminPassMasked(false);
      }
      if (cfg.user_password === MASK) {
        setUserPass(MASK);
        setUserPassMasked(true);
      } else {
        setUserPass(cfg.user_password || '');
        setUserPassMasked(false);
      }
      if (cfg.mqtt_password === MASK) {
        setMqttPass(MASK);
        setMqttPassMasked(true);
      } else {
        setMqttPass(cfg.mqtt_password || '');
        setMqttPassMasked(false);
      }
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      const status = e?.response?.status;
      setError(
        typeof d === 'string'
          ? d
          : (status === 401
            ? t('settings.piletaDiscovery.errorAuth', {
              defaultValue: 'Sesión SHC inválida. Cerrá sesión y volvé a entrar; la config en servidor no se borró.',
            })
            : (e?.message || t('settings.piletaDiscovery.errorLoad', {
              defaultValue: 'No se pudo cargar la configuración',
            }))),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void refresh(); }, [refresh]);

  const blockDemo = () => {
    if (!isDemoUser) return false;
    onDemoActionAttempt?.();
    return true;
  };

  const handleTest = async (profile: PiletaCredProfile) => {
    if (blockDemo()) return;
    setTesting(profile);
    setError(null);
    const setter = profile === 'admin' ? setTestAdmin : setTestUser;
    setter(null);
    try {
      // Guardar primero si hay cambios de password en claro (test usa BD)
      const portNum = Number(port) || 8080;
      const body: Record<string, unknown> = {
        display_name: displayName.trim() || 'Pileta / Riego',
        host: host.trim(),
        port: portNum,
        admin_username: adminUser.trim() || 'admin',
        user_username: userUser.trim() || 'user',
        mqtt_enabled: mqttEnabled,
        mqtt_port: Number(mqttPort) || 1883,
        mqtt_username: mqttUser.trim() || 'admin',
        mqtt_topic_pwa: mqttTopic.trim() || 'pileta-riego/arduino/estado/pwa',
      };
      if (habitacionId !== '') body.habitacion_id = habitacionId;
      if (adminPass && adminPass !== MASK) body.admin_password = adminPass;
      if (userPass && userPass !== MASK) body.user_password = userPass;
      if (mqttPass && mqttPass !== MASK) body.mqtt_password = mqttPass;
      await putPiletaConfig(body);
      const res = await testPiletaCredentials(profile);
      if (res.ok) {
        setter(t('settings.piletaDiscovery.testOk', {
          defaultValue: 'OK{{name}}',
          name: res.user_name ? ` (${res.user_name})` : '',
        }));
      } else {
        setter(t('settings.piletaDiscovery.testFail', {
          defaultValue: 'Falló: {{error}}',
          error: res.error || 'error',
        }));
      }
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setter(typeof d === 'string' ? d : t('settings.piletaDiscovery.testFail', {
        defaultValue: 'Falló: {{error}}',
        error: e?.message || 'error',
      }));
    } finally {
      setTesting(null);
    }
  };

  const buildMqttBody = () => {
    const mqttPortNum = Number(mqttPort);
    const body: Record<string, unknown> = {
      mqtt_enabled: mqttEnabled,
      mqtt_username: mqttUser.trim() || 'admin',
      mqtt_topic_pwa: mqttTopic.trim() || 'pileta-riego/arduino/estado/pwa',
    };
    if (Number.isFinite(mqttPortNum) && mqttPortNum >= 1 && mqttPortNum <= 65535) {
      body.mqtt_port = mqttPortNum;
    }
    if (mqttPass && !(mqttPassMasked && mqttPass === MASK) && mqttPass !== MASK) {
      body.mqtt_password = mqttPass;
    }
    return body;
  };

  const handleTestMqtt = async () => {
    if (blockDemo()) return;
    setTestingMqtt(true);
    setTestMqttMsg(null);
    setError(null);
    try {
      const mqttPortNum = Number(mqttPort) || 1883;
      await putPiletaConfig({
        host: host.trim(),
        port: Number(port) || 8080,
        ...buildMqttBody(),
        mqtt_port: mqttPortNum,
      });
      const res = await testPiletaMqtt({
        host: host.trim(),
        mqtt_port: mqttPortNum,
        mqtt_username: mqttUser.trim() || 'admin',
        mqtt_password: mqttPass && mqttPass !== MASK ? mqttPass : undefined,
        mqtt_topic_pwa: mqttTopic.trim() || undefined,
      });
      if (res.ok) {
        setTestMqttMsg(t('settings.piletaDiscovery.mqttTestOk', {
          defaultValue: 'OK — conectado{{msg}}',
          msg: res.message_received
            ? t('settings.piletaDiscovery.mqttTestMsg', { defaultValue: ' (mensaje recibido)' })
            : '',
        }));
      } else {
        setTestMqttMsg(t('settings.piletaDiscovery.mqttTestFail', {
          defaultValue: 'Falló: {{error}}',
          error: res.error || 'error',
        }));
      }
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setTestMqttMsg(t('settings.piletaDiscovery.mqttTestFail', {
        defaultValue: 'Falló: {{error}}',
        error: typeof d === 'string' ? d : (e?.message || 'error'),
      }));
    } finally {
      setTestingMqtt(false);
    }
  };

  const handleImportMqtt = async () => {
    if (blockDemo()) return;
    setImportingMqtt(true);
    setError(null);
    setOkMsg(null);
    try {
      // Persistir host/creds web actuales para que el backend pueda loguear en la Pi
      const portNum = Number(port) || 8080;
      const body: Record<string, unknown> = {
        host: host.trim(),
        port: portNum,
        admin_username: adminUser.trim() || 'admin',
      };
      if (adminPass && adminPass !== MASK) body.admin_password = adminPass;
      await putPiletaConfig(body);
      const res = await suggestPiletaMqttFromPi();
      if (res.mqtt_port) setMqttPort(String(res.mqtt_port));
      setOkMsg(t('settings.piletaDiscovery.mqttImportOk', {
        defaultValue: 'Importado: Mega y SHC → {{host}}:{{port}}. {{note}}',
        host: res.mqtt_host_effective || host.trim() || '—',
        port: res.mqtt_port ?? '—',
        note: res.note || '',
      }));
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (e?.message || t('settings.piletaDiscovery.mqttImportFail', {
        defaultValue: 'No se pudo importar desde la Pi',
      })));
    } finally {
      setImportingMqtt(false);
    }
  };

  const handleSave = async () => {
    if (blockDemo()) return;
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      const portNum = Number(port);
      if (!Number.isFinite(portNum) || portNum < 1 || portNum > 65535) {
        setError(t('settings.piletaDiscovery.errorPort', { defaultValue: 'Puerto inválido' }));
        return;
      }
      const mqttPortNum = Number(mqttPort);
      if (!Number.isFinite(mqttPortNum) || mqttPortNum < 1 || mqttPortNum > 65535) {
        setError(t('settings.piletaDiscovery.errorMqttPort', { defaultValue: 'Puerto MQTT inválido' }));
        return;
      }
      const body: Record<string, unknown> = {
        display_name: displayName.trim() || 'Pileta / Riego',
        host: host.trim(),
        port: portNum,
        admin_username: adminUser.trim() || 'admin',
        user_username: userUser.trim() || 'user',
        enabled: true,
        ...buildMqttBody(),
        mqtt_port: mqttPortNum,
      };
      if (habitacionId !== '') body.habitacion_id = habitacionId;
      if (adminPass && !(adminPassMasked && adminPass === MASK)) {
        if (adminPass !== MASK) body.admin_password = adminPass;
      }
      if (userPass && !(userPassMasked && userPass === MASK)) {
        if (userPass !== MASK) body.user_password = userPass;
      }
      await putPiletaConfig(body);
      if (habitacionId === '') {
        await patchPiletaDevice({ clear_habitacion: true });
      }
      await cargarPileta();
      eventBus.emit('estructura:refrescar');
      setOkMsg(t('settings.piletaDiscovery.saved', { defaultValue: 'Configuración guardada' }));
      await refresh();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (e?.message || t('settings.piletaDiscovery.errorSave', {
        defaultValue: 'No se pudo guardar',
      })));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  return (
    <Box sx={{ color: 'white', px: 0.5, pb: 2, touchAction: 'pan-y' }}>
      <Typography sx={{ fontSize: '0.75rem', color: '#999', mb: 1.25, lineHeight: 1.4 }}>
        {t('settings.piletaDiscovery.help', {
          defaultValue: 'Configurá la Raspberry del tablero (IP, puerto HTTP, credenciales web, MQTT de chips y habitación).',
        })}
      </Typography>

      {/* Conexión / tarjeta */}
      <Box sx={sectionSx}>
        <Typography sx={sectionTitleSx}>
          {t('settings.piletaDiscovery.title', { defaultValue: 'Pileta / Riego' })}
        </Typography>
        <TextField
          fullWidth
          size="small"
          label={t('settings.piletaDiscovery.displayName', { defaultValue: 'Título de la tarjeta' })}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label={t('settings.piletaDiscovery.host', { defaultValue: 'Host / IP' })}
          value={host}
          onChange={(e) => setHost(e.target.value)}
          placeholder="192.168.1.50"
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label={t('settings.piletaDiscovery.port', { defaultValue: 'Puerto HTTP (PWA)' })}
          value={port}
          onChange={(e) => setPort(e.target.value)}
          inputProps={{ inputMode: 'numeric' }}
          InputLabelProps={{ shrink: true }}
          sx={{ ...fieldSx, mb: 0 }}
        />
      </Box>

      {/* Credenciales admin web */}
      <Box sx={sectionSx}>
        <Typography sx={sectionTitleSx}>
          {t('settings.piletaDiscovery.adminCreds', { defaultValue: 'Credenciales admin (web)' })}
        </Typography>
        <TextField
          fullWidth
          size="small"
          label={t('settings.piletaDiscovery.adminUser', { defaultValue: 'Usuario admin' })}
          value={adminUser}
          onChange={(e) => setAdminUser(e.target.value)}
          autoComplete="off"
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            type="password"
            label={t('settings.piletaDiscovery.adminPass', { defaultValue: 'Contraseña admin' })}
            value={adminPass}
            onChange={(e) => {
              setAdminPass(e.target.value);
              setAdminPassMasked(false);
            }}
            onFocus={() => {
              if (adminPassMasked && adminPass === MASK) {
                setAdminPass('');
                setAdminPassMasked(false);
              }
            }}
            autoComplete="new-password"
            InputLabelProps={{ shrink: true }}
            sx={{ ...fieldSx, mb: 0, flex: 1 }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => void handleTest('admin')}
            disabled={busy || testing != null}
            sx={{
              color: ACCENT,
              borderColor: ACCENT,
              textTransform: 'none',
              minWidth: 72,
              flexShrink: 0,
              height: 40,
            }}
          >
            {testing === 'admin'
              ? <CircularProgress size={16} sx={{ color: ACCENT }} />
              : t('settings.piletaDiscovery.test', { defaultValue: 'Probar' })}
          </Button>
        </Box>
        {testAdmin && (
          <Typography sx={{
            fontSize: '0.72rem',
            color: testAdmin.startsWith('OK') || testAdmin.includes('OK') ? '#66bb6a' : '#e57373',
            mt: 1,
          }}>
            {testAdmin}
          </Typography>
        )}
      </Box>

      {/* Credenciales user web */}
      <Box sx={sectionSx}>
        <Typography sx={sectionTitleSx}>
          {t('settings.piletaDiscovery.userCreds', { defaultValue: 'Credenciales user (web)' })}
        </Typography>
        <TextField
          fullWidth
          size="small"
          label={t('settings.piletaDiscovery.userUser', { defaultValue: 'Usuario user' })}
          value={userUser}
          onChange={(e) => setUserUser(e.target.value)}
          autoComplete="off"
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            type="password"
            label={t('settings.piletaDiscovery.userPass', { defaultValue: 'Contraseña user' })}
            value={userPass}
            onChange={(e) => {
              setUserPass(e.target.value);
              setUserPassMasked(false);
            }}
            onFocus={() => {
              if (userPassMasked && userPass === MASK) {
                setUserPass('');
                setUserPassMasked(false);
              }
            }}
            autoComplete="new-password"
            InputLabelProps={{ shrink: true }}
            sx={{ ...fieldSx, mb: 0, flex: 1 }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => void handleTest('user')}
            disabled={busy || testing != null}
            sx={{
              color: ACCENT,
              borderColor: ACCENT,
              textTransform: 'none',
              minWidth: 72,
              flexShrink: 0,
              height: 40,
            }}
          >
            {testing === 'user'
              ? <CircularProgress size={16} sx={{ color: ACCENT }} />
              : t('settings.piletaDiscovery.test', { defaultValue: 'Probar' })}
          </Button>
        </Box>
        {testUser && (
          <Typography sx={{
            fontSize: '0.72rem',
            color: testUser.startsWith('OK') || testUser.includes('OK') ? '#66bb6a' : '#e57373',
            mt: 1,
          }}>
            {testUser}
          </Typography>
        )}
      </Box>

      {/* MQTT chips */}
      <Box sx={sectionSx}>
        <Typography sx={sectionTitleSx}>
          {t('settings.piletaDiscovery.mqttSection', { defaultValue: 'MQTT — Actualizaciones tiempo real' })}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#888', mb: 1.25, lineHeight: 1.4 }}>
          {t('settings.piletaDiscovery.mqttHelp', {
            defaultValue: 'Broker MQTT del tablero de piscina',
          })}
        </Typography>
        <FormControlLabel
          control={(
            <Switch
              checked={mqttEnabled}
              onChange={(e) => setMqttEnabled(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT },
              }}
            />
          )}
          label={t('settings.piletaDiscovery.mqttEnabled', { defaultValue: 'Habilitar MQTT (actualizaciones tiempo real)' })}
          sx={{ color: '#ccc', mb: 1.25, ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
        />
        <TextField
          fullWidth
          size="small"
          label={t('settings.piletaDiscovery.mqttPort', { defaultValue: 'Puerto MQTT' })}
          value={mqttPort}
          onChange={(e) => setMqttPort(e.target.value)}
          disabled={!mqttEnabled}
          inputProps={{ inputMode: 'numeric' }}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label={t('settings.piletaDiscovery.mqttUser', { defaultValue: 'Usuario MQTT' })}
          value={mqttUser}
          onChange={(e) => setMqttUser(e.target.value)}
          disabled={!mqttEnabled}
          autoComplete="off"
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <TextField
          fullWidth
          size="small"
          type="password"
          label={t('settings.piletaDiscovery.mqttPass', { defaultValue: 'Contraseña MQTT' })}
          value={mqttPass}
          placeholder={mqttPassMasked ? undefined : 'pileta'}
          onChange={(e) => {
            setMqttPass(e.target.value);
            setMqttPassMasked(false);
          }}
          onFocus={() => {
            if (mqttPassMasked && mqttPass === MASK) {
              setMqttPass('');
              setMqttPassMasked(false);
            }
          }}
          disabled={!mqttEnabled}
          autoComplete="new-password"
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label={t('settings.piletaDiscovery.mqttTopic', { defaultValue: 'Topic estado PWA' })}
          value={mqttTopic}
          onChange={(e) => setMqttTopic(e.target.value)}
          disabled={!mqttEnabled}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => void handleTestMqtt()}
            disabled={busy || !mqttEnabled || testingMqtt || !host.trim()}
            sx={{
              backgroundColor: ACCENT,
              textTransform: 'none',
              minWidth: 110,
              '&:hover': { backgroundColor: '#1a7ae0' },
            }}
          >
            {testingMqtt
              ? <CircularProgress size={16} sx={{ color: '#fff' }} />
              : t('settings.piletaDiscovery.mqttTest', { defaultValue: 'Probar MQTT' })}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => void handleImportMqtt()}
            disabled={busy || importingMqtt || !host.trim()}
            sx={{ color: '#bbb', borderColor: '#555', textTransform: 'none', minWidth: 130 }}
          >
            {importingMqtt
              ? <CircularProgress size={16} sx={{ color: '#bbb' }} />
              : t('settings.piletaDiscovery.mqttImport', { defaultValue: 'Importar desde Pi' })}
          </Button>
        </Box>
        {testMqttMsg && (
          <Typography sx={{
            fontSize: '0.72rem',
            color: testMqttMsg.startsWith('OK') || testMqttMsg.includes('OK') ? '#66bb6a' : '#e57373',
            mt: 1,
          }}>
            {testMqttMsg}
          </Typography>
        )}
      </Box>

      {/* Habitación */}
      <Box sx={sectionSx}>
        <Typography sx={sectionTitleSx}>
          {t('settings.piletaDiscovery.room', { defaultValue: 'Habitación' })}
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 0 }}>
          <InputLabel id="pileta-room-label" shrink sx={{ color: '#999' }}>
            {t('settings.piletaDiscovery.room', { defaultValue: 'Habitación' })}
          </InputLabel>
          <Select
            labelId="pileta-room-label"
            label={t('settings.piletaDiscovery.room', { defaultValue: 'Habitación' })}
            notched
            value={habitacionId === '' ? '' : habitacionId}
            onChange={(e) => {
              const v = e.target.value;
              setHabitacionId(v === '' ? '' : Number(v));
            }}
            sx={{
              color: 'white',
              backgroundColor: '#0f0f0f',
              '.MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
              '.MuiSvgIcon-root': { color: '#bbb' },
            }}
            MenuProps={{
              PaperProps: { sx: { bgcolor: '#2F3235', color: 'white' } },
            }}
          >
            <MenuItem value="">
              <em>{t('settings.piletaDiscovery.roomNone', { defaultValue: 'Sin asignar' })}</em>
            </MenuItem>
            {rooms.map((r) => (
              <MenuItem key={r.id} value={r.id}>{r.nombre}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={() => void handleSave()}
        disabled={busy || !host.trim()}
        sx={{
          backgroundColor: ACCENT,
          textTransform: 'none',
          mb: 1,
          '&:hover': { backgroundColor: '#1a7ae0' },
        }}
      >
        {busy
          ? <CircularProgress size={18} sx={{ color: '#fff' }} />
          : t('settings.piletaDiscovery.save', { defaultValue: 'Guardar' })}
      </Button>

      {okMsg && <Typography sx={{ color: '#66bb6a', fontSize: '0.8rem', mt: 0.5 }}>{okMsg}</Typography>}
      {error && <Typography sx={{ color: '#e57373', fontSize: '0.8rem', mt: 0.5 }}>{String(error)}</Typography>}
    </Box>
  );
};

export default PiletaDiscoveryTab;
